import api from './api';

export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  template_type: string;
  owner_type: string;
  config: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
}

export interface GeneratedReport {
  id: string;
  template_id?: string;
  title: string;
  category: string;
  format: string;
  status: string;
  hotel_id?: string;
  generated_by: string;
  generated_by_role: string;
  period_start?: string;
  period_end?: string;
  file_size_bytes: number;
  error_message?: string;
  approved_by?: string;
  approved_at?: string;
  archived: boolean;
  created_at: string;
  completed_at?: string;
}

export interface GenerateReportRequest {
  template_id?: string;
  title: string;
  category: string;
  format: string;
  period_start?: string;
  period_end?: string;
  parameters?: Record<string, unknown>;
}

export interface ReportStats {
  total_generated: number;
  total_completed: number;
  total_failed: number;
  total_approved: number;
  by_category: Record<string, number>;
  by_format: Record<string, number>;
}

const BASE_URL = '/api/v1/reports';

export const reportService = {
  async getTemplates(category?: string): Promise<ReportTemplate[]> {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    const response = await api.get(`${BASE_URL}/templates?${params}`);
    return response.data.data || [];
  },

  async getTemplate(id: string): Promise<ReportTemplate> {
    const response = await api.get(`${BASE_URL}/templates/${id}`);
    return response.data.data;
  },

  async generateReport(request: GenerateReportRequest): Promise<GeneratedReport> {
    const response = await api.post(`${BASE_URL}/generate`, request);
    return response.data.data;
  },

  async getReports(category?: string, status?: string): Promise<GeneratedReport[]> {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (status) params.append('status', status);
    const response = await api.get(`${BASE_URL}?${params}`);
    return response.data.data || [];
  },

  async getReport(id: string): Promise<GeneratedReport> {
    const response = await api.get(`${BASE_URL}/${id}`);
    return response.data.data;
  },

  async downloadReport(id: string): Promise<void> {
    const response = await api.get(`${BASE_URL}/${id}/download`, {
      responseType: 'blob',
    });
    
    // Get filename from content-disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'report';
    if (contentDisposition) {
      const encoded = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      const plain = contentDisposition.match(/filename="([^"]+)"|filename=([^;]+)/i);
      try { filename = encoded ? decodeURIComponent(encoded[1]) : plain ? (plain[1] || plain[2]).trim() : filename; } catch { /* Keep the safe fallback for malformed headers. */ }
      filename = filename.replace(/[\\/]/g, '_');
    }

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async archiveReport(id: string): Promise<void> {
    await api.post(`${BASE_URL}/${id}/archive`);
  },

  async deleteReport(id: string): Promise<void> {
    await api.delete(`${BASE_URL}/${id}`);
  },

  async getStats(startDate?: string, endDate?: string): Promise<ReportStats> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const response = await api.get(`${BASE_URL}/stats?${params}`);
    return response.data.data;
  },
};

export default reportService;
